import { Box, Group, Input, Spacer, Stack, Separator, Tabs, Textarea, HStack, Text } from '@chakra-ui/react'
import type { FocusEventHandler, FC, FormEventHandler } from 'react'
import { forwardRef } from 'react'
import { useState, useRef } from 'react'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import {
  DialogBody,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@app/components/ui/dialog'
import { Field } from '@app/components/ui/field'
import { toaster } from '@app/components/ui/toaster'
import useGetObject from '@app/hooks/useGetObject'
import LabelAutocompleteInput from '@app/next/forms/autocomplete/labelAutocompleteInput'
import SlackChannelAutocomplete from '@app/next/forms/autocomplete/slackChannelAutocomplete'
import FormAlert from '@app/next/forms/formAlert'
import ToggleInput from '@app/next/forms/toggleInput'
import AiPopulateStrategyDescriptionButton from '@app/pages/strategies/components/settings/aiPopulateStrategyDescriptionButton'
import AiPopulateStrategyNameButton from '@app/pages/strategies/components/settings/aiPopulateStrategyNameButton'
import AmplitudeBulkCharts from '@app/pages/strategies/components/settings/components/amplitudeBulkCharts'
import DownloadDataButtons from '@app/pages/strategies/components/settings/components/downloadDataButtons'
import PopulateMetricsWithDummyData from '@app/pages/strategies/components/settings/components/populateMetricsWithDummyData'
import SendStrategyReviewButton from '@app/pages/strategies/components/settings/components/sendStrategyReviewButton'
import StrategyDeleteButton from '@app/pages/strategies/components/strategyDeleteButton'
import Can from '@app/shared/authorization/can'
import { useStore } from '@app/store'
import type { DomainStrategy } from '@app/types'
import unpackErrors from '@app/utils/unpackErrors'
import { useStrategyBulkImportUrlsMutation } from '@graphql/queries'

interface Props {
  open?: boolean
  onOpenChange?: (e) => void
}

const NameLabel = forwardRef<HTMLInputElement, { strategyId: string }>(({ strategyId }, ref) => {
  if (!strategyId) {
    return <Text>Name</Text>
  }

  return (
    <HStack gap={1}>
      <Text>Name</Text>
      <Spacer />
      <AiPopulateStrategyNameButton data-cy="strategy-name-suggest" strategyId={strategyId} ref={ref} />
    </HStack>
  )
})

const DescriptionLabel = forwardRef<HTMLTextAreaElement, { strategyId: string }>(({ strategyId }, ref) => {
  if (!strategyId) {
    return <Text>Description</Text>
  }

  return (
    <HStack gap={1}>
      <Text>Description</Text>
      <Spacer />
      <AiPopulateStrategyDescriptionButton data-cy="strategy-description-suggest" strategyId={strategyId} ref={ref} />
    </HStack>
  )
})

const StrategySettings: FC<Props> = ({ open = true, onOpenChange = () => {} }) => {
  const [loading, setLoading] = useState(false)
  const { strategyId } = useParams()
  const strategy = useGetObject(strategyId, 'strategy')
  const updateObject = useStore.use.updateObject()
  const navigate = useNavigate()
  const [errors, setErrors] = useState<ReturnType<typeof unpackErrors>>({})
  const [, importUrls] = useStrategyBulkImportUrlsMutation()
  const nameRef = useRef<HTMLInputElement>(null)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)

  const navigateClose = useCallback(() => {
    navigate('..')
  }, [navigate])

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()

      const formData = new FormData(e.currentTarget)
      const formValues = Object.fromEntries(formData.entries())
      const values: { id: string; labels?: string[]; urls?: string } = { ...formValues, id: strategyId }
      values.labels &&= JSON.parse(values.labels as unknown as string)

      const urls = values.urls
      delete values.urls

      setLoading(true)
      try {
        await updateObject({ strategy: values }).then((response) => {
          const { errors: updateErrors, nodeObjects } = response?.data?.domainObjectUpdate || {}

          if (updateErrors.length) {
            setErrors(unpackErrors(updateErrors))
            return
          }

          if (nodeObjects[0]) {
            const { labels } = nodeObjects[0] as DomainStrategy

            // @ts-expect-error labels are a massive time suck and should become real objects
            updateObject({ strategy: { id: strategyId, labels } }, true)
          }
        })

        if (urls) {
          await importUrls({
            input: {
              strategyId: strategy.id,
              urls: urls.split('\n')
            }
          }).then(() => {
            toaster.create({
              title: 'Adding URLs',
              description: 'The URLs you have added will be added to the map shortly.',
              type: 'info'
            })
          })
        }

        toaster.create({
          title: 'Updated Strategy',
          description: 'Successfully updated the strategy.',
          type: 'success'
        })

        onOpenChange({ open: false })

        navigateClose()
      } catch (error) {
        setErrors(unpackErrors([error]))
      } finally {
        setLoading(false)
      }
    },
    [importUrls, navigateClose, onOpenChange, strategy.id, strategyId, updateObject]
  )

  const updateField = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      const updateValue = {
        strategy: { id: strategyId, [e.target.name]: e.target.value }
      }

      return updateObject(updateValue)
    },
    [strategyId, updateObject]
  )

  return (
    <DialogRoot
      onOpenChange={onOpenChange}
      onEscapeKeyDown={navigateClose}
      onPointerDownOutside={navigateClose}
      open={open}
      size="xl"
    >
      <DialogBackdrop />
      <DialogContent>
        <form id="strategy-settings-form" onSubmit={onSubmit}>
          <DialogCloseTrigger onClick={() => navigate('..')} />
          <DialogHeader>
            <DialogTitle>Map Settings</DialogTitle>
          </DialogHeader>
          <DialogBody px={6} bg="bg">
            <FormAlert description={errors?.global?.message} title="Failed to update the strategy!" />
            <Tabs.Root fitted defaultValue="general">
              <Tabs.List>
                <Tabs.Trigger value="general">General</Tabs.Trigger>
                <Can I="update" a="metricSource">
                  <Tabs.Trigger value="data">Data</Tabs.Trigger>
                </Can>
                <Tabs.Trigger value="downloads">Downloads</Tabs.Trigger>
                <Tabs.Trigger value="ai">AI</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="general">
                <Stack gap={4} separator={<Separator />}>
                  <Stack gap={4}>
                    <Field
                      label={<NameLabel strategyId={strategy.id} ref={nameRef} />}
                      invalid={!!errors?.name}
                      errorText={errors?.name?.message}
                    >
                      <Input
                        ref={nameRef}
                        defaultValue={strategy.name}
                        name="name"
                        onBlur={updateField}
                        placeholder="Map name"
                      />
                    </Field>
                    <Field
                      label={<DescriptionLabel strategyId={strategy.id} ref={descriptionRef} />}
                      invalid={!!errors?.description}
                      errorText={errors?.description?.message}
                    >
                      <Textarea
                        ref={descriptionRef}
                        defaultValue={strategy.description}
                        name="description"
                        placeholder="Describe the map."
                      />
                    </Field>
                    <LabelAutocompleteInput
                      name="labels"
                      label="Labels"
                      defaultValue={strategy?.labels || []}
                      menuPortalTarget={document.body}
                    />
                    <Stack gap={4}>
                      <Field
                        label="Slack channel"
                        helperText="Choose a Slack channel below to have a weekly Slack digest delivered each Friday at 9 a.m. PT."
                        invalid={!!errors?.slackChannelId}
                        errorText={errors?.slackChannelId?.message}
                      >
                        <SlackChannelAutocomplete
                          name="slackChannelId"
                          defaultValue={strategy?.slackChannelId}
                          clearable
                        />
                      </Field>
                      {strategy.slackChannelId && <SendStrategyReviewButton strategy={strategy} />}
                    </Stack>
                  </Stack>
                  <Box css={{ '--field-label-width': '220px' }}>
                    <Field
                      label="Show data science analysis"
                      orientation="horizontal"
                      invalid={!!errors?.showCorrelations}
                      errorText={errors?.showCorrelations?.message}
                    >
                      <ToggleInput name="showCorrelations" defaultValue={strategy.showCorrelations} />
                    </Field>
                  </Box>
                </Stack>
              </Tabs.Content>
              <Can I="update" a="metricSource">
                <Tabs.Content value="data">
                  <Stack gap={4} separator={<Separator />}>
                    <AmplitudeBulkCharts />
                    <PopulateMetricsWithDummyData strategy={strategy} />
                  </Stack>
                </Tabs.Content>
              </Can>
              <Tabs.Content value="downloads">
                <DownloadDataButtons strategy={strategy} />
              </Tabs.Content>
              <Tabs.Content value="ai">
                <Stack gap={4} separator={<Separator />}>
                  <Field label="Context">
                    <Textarea
                      defaultValue={strategy.aiContext}
                      name="aiContext"
                      placeholder="Provide information that would be useful for our AI assistants to know."
                    />
                  </Field>
                </Stack>
              </Tabs.Content>
            </Tabs.Root>
          </DialogBody>
          <DialogFooter>
            <StrategyDeleteButton strategyId={strategy.id} />
            <Spacer />
            <Group>
              <Button onClick={navigateClose} variant="outline">
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                Save
              </Button>
            </Group>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  )
}

export default StrategySettings
